/**
 * Highlighter léger à base de regex — aucune dépendance externe.
 *
 * Deux modes :
 *  - `sql` : mots-clés, fonctions, chaînes, nombres, commentaires, opérateurs.
 *  - `log` : niveaux (ERROR/WARN/INFO/DEBUG), timestamps.
 *
 * Sortie : soit une liste de segments tokenisés (`tokenize`), soit du HTML
 * échappé prêt à injecter via `innerHTML` (`highlightToHtml`). Tout le contenu
 * utilisateur est échappé AVANT toute construction de balise — aucune chaîne
 * non échappée ne transite vers le DOM.
 */

export type TokenClass =
  | "keyword"
  | "function"
  | "string"
  | "number"
  | "comment"
  | "operator"
  | "punctuation"
  | "error"
  | "warn"
  | "info"
  | "debug"
  | "timestamp"
  | "text";

export interface Token {
  /** Texte brut (NON échappé). */
  text: string;
  /** Classe de coloration ; `text` = aucun style particulier. */
  cls: TokenClass;
}

export type HighlightMode = "sql" | "log";

// ── Échappement HTML ───────────────────────────────────────────────────────

const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/** Échappe une chaîne pour insertion sûre dans du HTML (anti-XSS). */
export function escapeHtml(input: string): string {
  return input.replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch] ?? ch);
}

// ── SQL ────────────────────────────────────────────────────────────────────

const SQL_KEYWORDS = new Set<string>([
  "select",
  "from",
  "where",
  "join",
  "inner",
  "left",
  "right",
  "full",
  "outer",
  "cross",
  "on",
  "as",
  "and",
  "or",
  "not",
  "in",
  "is",
  "null",
  "like",
  "between",
  "exists",
  "group",
  "by",
  "having",
  "order",
  "asc",
  "desc",
  "limit",
  "offset",
  "top",
  "distinct",
  "union",
  "all",
  "insert",
  "into",
  "values",
  "update",
  "set",
  "delete",
  "create",
  "alter",
  "drop",
  "table",
  "view",
  "index",
  "database",
  "schema",
  "primary",
  "key",
  "foreign",
  "references",
  "constraint",
  "default",
  "unique",
  "check",
  "with",
  "case",
  "when",
  "then",
  "else",
  "end",
  "begin",
  "commit",
  "rollback",
  "transaction",
  "declare",
  "exec",
  "execute",
  "procedure",
  "function",
  "trigger",
  "returns",
  "return",
  "if",
  "while",
  "for",
  "loop",
  "use",
  "go",
  "grant",
  "revoke",
  "truncate",
  "add",
  "column",
  "modify",
  "cascade",
  "restrict",
  "using",
  "natural",
  "into",
  "output",
  "merge",
  "over",
  "partition",
  "row_number",
  "rank",
  "dense_rank",
  "nulls",
  "first",
  "last",
]);

const SQL_FUNCTIONS = new Set<string>([
  "count",
  "sum",
  "avg",
  "min",
  "max",
  "coalesce",
  "cast",
  "convert",
  "isnull",
  "nullif",
  "len",
  "length",
  "substring",
  "substr",
  "trim",
  "ltrim",
  "rtrim",
  "upper",
  "lower",
  "replace",
  "concat",
  "getdate",
  "now",
  "current_timestamp",
  "datediff",
  "dateadd",
  "datepart",
  "year",
  "month",
  "day",
  "abs",
  "round",
  "ceiling",
  "floor",
  "power",
  "sqrt",
  "format",
  "string_agg",
  "char",
  "ascii",
]);

/**
 * Tokenise une portion de texte SQL.
 *
 * Stratégie : un scanner monolithique parcourt la source caractère par
 * caractère et émet des tokens. Pas de regex globale fragile, ce qui garantit
 * qu'aucun caractère n'est perdu ou dupliqué (essentiel pour l'overlay).
 */
function tokenizeSql(src: string): Token[] {
  const out: Token[] = [];
  const n = src.length;
  let i = 0;

  const push = (text: string, cls: TokenClass): void => {
    if (text.length === 0) return;
    out.push({ text, cls });
  };

  while (i < n) {
    const ch = src[i];

    // Espaces et retours ligne → token `text` (préserve la mise en page).
    if (ch === " " || ch === "\t" || ch === "\n" || ch === "\r") {
      let j = i + 1;
      while (j < n && /[ \t\n\r]/.test(src[j])) j++;
      push(src.slice(i, j), "text");
      i = j;
      continue;
    }

    // Commentaire ligne  -- ...
    if (ch === "-" && src[i + 1] === "-") {
      let j = i + 2;
      while (j < n && src[j] !== "\n") j++;
      push(src.slice(i, j), "comment");
      i = j;
      continue;
    }

    // Commentaire bloc  /* ... */
    if (ch === "/" && src[i + 1] === "*") {
      let j = i + 2;
      while (j < n && !(src[j] === "*" && src[j + 1] === "/")) j++;
      j = Math.min(n, j + 2);
      push(src.slice(i, j), "comment");
      i = j;
      continue;
    }

    // Chaîne  '...'  (doublement de quote = échappement SQL)
    if (ch === "'") {
      let j = i + 1;
      while (j < n) {
        if (src[j] === "'" && src[j + 1] === "'") {
          j += 2;
          continue;
        }
        if (src[j] === "'") {
          j++;
          break;
        }
        j++;
      }
      push(src.slice(i, j), "string");
      i = j;
      continue;
    }

    // Identifiant entre crochets [col] ou guillemets "col" → string-like.
    if (ch === "[") {
      let j = i + 1;
      while (j < n && src[j] !== "]") j++;
      j = Math.min(n, j + 1);
      push(src.slice(i, j), "string");
      i = j;
      continue;
    }
    if (ch === '"') {
      let j = i + 1;
      while (j < n && src[j] !== '"') j++;
      j = Math.min(n, j + 1);
      push(src.slice(i, j), "string");
      i = j;
      continue;
    }

    // Nombre
    if (/[0-9]/.test(ch) || (ch === "." && /[0-9]/.test(src[i + 1] ?? ""))) {
      let j = i + 1;
      while (j < n && /[0-9.eExX+\-a-fA-F]/.test(src[j])) {
        // arrêt si le `+`/`-` n'est pas un exposant
        if ((src[j] === "+" || src[j] === "-") && !/[eE]/.test(src[j - 1])) break;
        j++;
      }
      push(src.slice(i, j), "number");
      i = j;
      continue;
    }

    // Identifiant / mot-clé
    if (/[A-Za-z_@#]/.test(ch)) {
      let j = i + 1;
      while (j < n && /[A-Za-z0-9_@#$]/.test(src[j])) j++;
      const word = src.slice(i, j);
      const lower = word.toLowerCase();
      // une fonction est suivie (éventuellement après espaces) d'une parenthèse
      let k = j;
      while (k < n && /[ \t]/.test(src[k])) k++;
      const isCall = src[k] === "(";
      if (SQL_KEYWORDS.has(lower)) push(word, "keyword");
      else if (SQL_FUNCTIONS.has(lower) || isCall) push(word, "function");
      else push(word, "text");
      i = j;
      continue;
    }

    // Opérateurs
    if (/[=<>!+\-*/%|&^~]/.test(ch)) {
      let j = i + 1;
      while (j < n && /[=<>!+\-*/%|&^~]/.test(src[j])) j++;
      push(src.slice(i, j), "operator");
      i = j;
      continue;
    }

    // Ponctuation
    if (/[(),;.]/.test(ch)) {
      push(ch, "punctuation");
      i++;
      continue;
    }

    // Tout le reste
    push(ch, "text");
    i++;
  }

  return out;
}

// ── Logs ───────────────────────────────────────────────────────────────────

const LOG_LEVEL_RE =
  /\b(ERROR|ERR|FATAL|CRITICAL|CRIT|EXCEPTION|FAIL(?:ED|URE)?|WARN(?:ING)?|INFO(?:RMATION)?|NOTICE|DEBUG|TRACE|VERBOSE)\b/i;

const LOG_TIMESTAMP_RE =
  /(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:[.,]\d+)?(?:Z|[+-]\d{2}:?\d{2})?|\d{2}:\d{2}:\d{2}(?:[.,]\d+)?)/;

/** Mappe un mot de niveau de log vers une classe de token. */
function logLevelClass(word: string): TokenClass {
  const w = word.toUpperCase();
  if (/^(ERR|ERROR|FATAL|CRIT|CRITICAL|EXCEPTION|FAIL)/.test(w)) return "error";
  if (/^(WARN|NOTICE)/.test(w)) return "warn";
  if (/^(INFO)/.test(w)) return "info";
  return "debug"; // DEBUG / TRACE / VERBOSE
}

/** Tokenise une seule ligne de log. */
function tokenizeLogLine(line: string): Token[] {
  const out: Token[] = [];
  const rest = line;

  // Découpe successive : timestamp en tête, puis niveau, puis reste.
  const combined = new RegExp(`(${LOG_TIMESTAMP_RE.source})|(${LOG_LEVEL_RE.source})`, "gi");

  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = combined.exec(rest)) !== null) {
    if (m.index > lastIndex) {
      out.push({ text: rest.slice(lastIndex, m.index), cls: "text" });
    }
    if (m[1] != null) {
      out.push({ text: m[0], cls: "timestamp" });
    } else {
      out.push({ text: m[0], cls: logLevelClass(m[0]) });
    }
    lastIndex = m.index + m[0].length;
    if (m[0].length === 0) combined.lastIndex++; // garde-fou anti-boucle
  }
  if (lastIndex < rest.length) {
    out.push({ text: rest.slice(lastIndex), cls: "text" });
  }
  return out;
}

/** Tokenise un bloc de logs multi-lignes (préserve les `\n`). */
function tokenizeLog(src: string): Token[] {
  const out: Token[] = [];
  const lines = src.split("\n");
  lines.forEach((line, idx) => {
    for (const tok of tokenizeLogLine(line)) out.push(tok);
    if (idx < lines.length - 1) out.push({ text: "\n", cls: "text" });
  });
  return out;
}

// ── API publique ───────────────────────────────────────────────────────────

/** Tokenise une source selon le mode demandé. */
export function tokenize(src: string, mode: HighlightMode): Token[] {
  if (typeof src !== "string" || src.length === 0) return [];
  return mode === "sql" ? tokenizeSql(src) : tokenizeLog(src);
}

/**
 * Produit du HTML échappé et coloré. Chaque token devient
 * `<span class="tok-…">texte échappé</span>` (ou le texte échappé brut pour
 * la classe `text`). Sûr pour `innerHTML`.
 */
export function highlightToHtml(src: string, mode: HighlightMode): string {
  const tokens = tokenize(src, mode);
  let html = "";
  for (const tok of tokens) {
    const safe = escapeHtml(tok.text);
    if (tok.cls === "text") {
      html += safe;
    } else {
      html += `<span class="tok-${tok.cls}">${safe}</span>`;
    }
  }
  return html;
}
